import type { MarketVariants } from '@/lib/i18n'

export type PaymentMethodCopy = {
  /** Row label on the slide. */
  label: string
  /** Panel title. */
  title: string
  intro: string
  whyScammers: string[]
  inPractice: string[]
  // Optional: gift cards skip this since the panel visual already shows brands.
  channels?: { heading: string; items: string[] }
  phrases: string[]
  rule: string
  ifAlreadyGone: string
}

export type Slide5PaymentCopy = {
  heading: string
  intro: string
  learnMore: string
  panelEyebrow: string
  /** aria-label on the panel close button. */
  closeLabel: string
  whyHeading: string
  whatHeading: string
  phrasesHeading: string
  /** Quote marks wrapped around each listened-for phrase. */
  phraseQuoteOpen: string
  phraseQuoteClose: string
  ruleLabel: string
  goneLabel: string

  // Gift card panel visual
  giftCardVisualTitle: string
  /** aria-label for each card tile. {brand} is replaced with the brand name. */
  giftCardAriaLabel: string
  giftCardVisualNote: string

  // Wire transfer panel visual (banking-app mockup)
  wireVisualTitle: string
  wireAppTitle: string
  wireSendTo: string
  wireExternalAccount: string
  /** Currency shown before the amount ("$"). Empty when the currency follows. */
  wireAmountPrefix: string
  wireAmountWhole: string
  wireAmountCents: string
  /** Currency shown after the amount (Arabic "ر.ق"). Empty otherwise. */
  wireAmountSuffix: string
  /** Label including its trailing space; the value follows inline. */
  wireRemainingLabel: string
  wireRemainingLimit: string
  wireFromLabel: string
  wireAccountName: string
  /** Label including its trailing space; the value follows inline. */
  wireAvailableLabel: string
  wireAccountAvailable: string
  wireDeliveryLabel: string
  wireDeliveryValue: string
  wireNewPayee: string
  /** Includes the trailing forward chevron character. */
  wireContinue: string
  wireFooter: string

  // The four payment methods, in row order.
  giftCard: PaymentMethodCopy
  transfer: PaymentMethodCopy
  crypto: PaymentMethodCopy
  cashPost: PaymentMethodCopy
}

export const copy: MarketVariants<Slide5PaymentCopy> = {
  US: {
    heading: 'Untraceable payment methods',
    intro:
      'Scammers use these payment methods because they are nearly impossible to reverse. No legitimate business or government agency will ever ask you to pay this way.',
    learnMore: 'Learn more',
    panelEyebrow: 'Payment warning',
    closeLabel: 'Close panel',
    whyHeading: 'Why scammers prefer this',
    whatHeading: 'What it looks like',
    phrasesHeading: 'Phrases to listen for',
    phraseQuoteOpen: '“',
    phraseQuoteClose: '”',
    ruleLabel: 'The rule',
    goneLabel: 'If money has already gone',

    giftCardVisualTitle: 'Cards scammers ask for',
    giftCardAriaLabel: '{brand} gift card',
    giftCardVisualNote:
      'These are the most-requested brands in scam reports. Scammers want them because the codes are redeemable worldwide and almost impossible to recover.',

    wireVisualTitle: 'How it might look in your banking app',
    wireAppTitle: 'Transfer Money',
    wireSendTo: 'Send to: Recipient’s Bank',
    wireExternalAccount: 'External account (…4873)',
    wireAmountPrefix: '$',
    wireAmountWhole: '3,000',
    wireAmountCents: '.00',
    wireAmountSuffix: '',
    wireRemainingLabel: 'Remaining transfer limit: ',
    wireRemainingLimit: '$5,000.00',
    wireFromLabel: 'From',
    wireAccountName: 'Premier eChecking (…1234)',
    wireAvailableLabel: 'Available to send: ',
    wireAccountAvailable: '$51,234.00',
    wireDeliveryLabel: 'Delivery',
    wireDeliveryValue: 'Instant',
    wireNewPayee: 'New payee. You have not sent here before.',
    wireContinue: 'Continue ›',
    wireFooter:
      'If a stranger is ever talking you through this screen, hang up and call your bank yourself.',

    giftCard: {
      label: 'Gift cards',
      title: 'Gift cards',
      intro:
        'Once you read out the code on the back, the money is gone in seconds. Cards are drained from anywhere in the world before you have left the shop.',
      whyScammers: [
        'The code on the back is the money. No bank account, no name, no trail.',
        'Available at every supermarket, gas station and pharmacy.',
        'Drained instantly and converted to crypto on resale sites.',
        'No buyer protection. No refund. No chargeback.',
      ],
      inPractice: [
        'A caller stays on the line while you drive to the shop.',
        'They tell you exactly which brand to buy, which value, and how many cards.',
        'They ask you to scratch the back and read the 16-digit code aloud.',
        'Sometimes they ask for a photo of the back of the card.',
      ],
      // No `channels` section here. The brand grid in the panel visual already
      // covers which gift cards scammers ask for.
      phrases: [
        'Stay on the line. Do not tell the cashier why you are buying these.',
        'If anyone asks, say it is a present for your grandson.',
        'Just read me the numbers on the back. I will text you a receipt.',
      ],
      rule:
        'No real company, court, government agency or utility ever accepts payment in gift cards. Not once. Not ever.',
      ifAlreadyGone:
        'Call the gift card brand fraud line straight away. Apple, Amazon and Google can sometimes freeze the balance if you call within the first hour. Keep the card and the receipt as evidence.',
    },
    transfer: {
      label: 'Wire transfers',
      title: 'Wire transfers',
      intro:
        'Bank transfers look ordinary, which is exactly why scammers use them. Once the receiving bank releases the funds, recall is rare.',
      whyScammers: [
        'Looks like a normal payment, so banks may not flag it.',
        'Money can clear in minutes and cross borders within hours.',
        'Funds are split across mule accounts the moment they land.',
        'Pressure gets the transfer away before you double-check.',
      ],
      inPractice: [
        'Caller pretends to be your bank fraud team, asking you to "move money to a safe account".',
        'Title company or real estate agent emails new wire instructions for a house closing.',
        'Romance partner needs emergency money for travel or a hospital bill.',
        'Investment "advisor" rushes a same-day transfer for a "limited window".',
      ],
      channels: {
        heading: 'Channels they ask you to use',
        items: [
          'ACH or wire transfer to a brand new payee',
          'Western Union or MoneyGram (cash pickup, hardest to recover)',
          'Zelle, Venmo or Cash App to a number you have not sent to before',
          'FedNow or RTP for instant transfers that cannot be reversed',
        ],
      },
      phrases: [
        'Do not tell your bank what it is for. They are part of the investigation.',
        'Move it now, you can move it back tomorrow.',
        'Use a new payee. The old account has been compromised.',
        'Mark the reason as "family" so it goes through faster.',
      ],
      rule:
        'Real banks never call to ask you to move money. Real title companies never email new wire instructions out of the blue. Anyone asking for either is a scammer.',
      ifAlreadyGone:
        'Call your bank fraud line in the same hour. Wire transfers can sometimes be recalled within 24 hours via SWIFT recall. Report the loss to the FBI IC3 (ic3.gov) and to the FTC at reportfraud.ftc.gov.',
    },
    crypto: {
      label: 'Cryptocurrency',
      title: 'Cryptocurrency',
      intro:
        'Crypto payments are final. There is no fraud department to reverse them and no name attached to the receiving wallet.',
      whyScammers: [
        'No reversal. Once it leaves your wallet, it is gone.',
        'Wallets are anonymous strings of letters, not accounts in someone’s name.',
        'Bitcoin ATMs convert your cash to crypto on the spot.',
        'Funds split across hundreds of wallets in seconds.',
      ],
      inPractice: [
        'Caller directs you to a Bitcoin ATM at a convenience store or gas station.',
        'They text you a QR code. You scan it, deposit cash, and the crypto goes straight to them.',
        '"Recovery agents" appear after a previous scam, asking for crypto to "unlock" your funds.',
        'Investment "advisor" on WhatsApp shows fake trading dashboards to keep you topping up.',
      ],
      channels: {
        heading: 'Where they send you',
        items: [
          'Bitcoin ATMs (Coinhub, BitAccess, BitDepot, often inside convenience stores)',
          'Cash App’s Bitcoin tab',
          'Coinbase, Binance or Kraken',
          'Wallet apps like Trust Wallet or MetaMask asked to scan a QR code',
        ],
      },
      phrases: [
        'Deposit the cash, then text me a photo of the receipt.',
        'Your money will be safer in a federal Bitcoin wallet.',
        'We can recover your lost crypto for a small gas fee.',
        'Do not speak to anyone in the shop about what you are doing.',
      ],
      rule:
        'No government, bank, utility or law enforcement agency accepts cryptocurrency. The Bitcoin machine at the convenience store is not a federal payment system.',
      ifAlreadyGone:
        'Save the receiving wallet address and the transaction ID. Report to your country’s fraud line and to the exchange if you know it. Never pay a "recovery service". They are nearly always the same scammers under a new name.',
    },
    cashPost: {
      label: 'Cash by post or courier',
      title: 'Cash by post or courier',
      intro:
        'Cash leaves no paper trail and no banking protection at all. Once it is in a courier’s hands, it cannot be recalled.',
      whyScammers: [
        'No bank, no record, no fraud team to call.',
        'Bypasses the "are you sure?" warnings cashiers and apps now show.',
        'Couriers rarely check what is inside a parcel.',
        'Once collected, the scammer simply disappears.',
      ],
      inPractice: [
        'Caller pretends to be police and asks you to withdraw savings to "help catch a fraudster".',
        'A taxi or "plain-clothes officer" is sent to collect cash from your home.',
        'You are asked to mail cash hidden inside a book, magazine or birthday card.',
        'Caller stays on the line during the bank visit and tells you what to say to staff.',
      ],
      channels: {
        heading: 'How they collect it',
        items: [
          'Couriers, often a local taxi firm, sent to your front door',
          'USPS, UPS or FedEx parcels with cash hidden inside other items',
          'Drop-off at a "safe" location like a station parking lot',
          'Handover to a "plain-clothes officer" on your doorstep',
        ],
      },
      phrases: [
        'Tell the cashier the money is for home improvements.',
        'The bills will be marked, and returned to you within 48 hours.',
        'An officer named [first name] will arrive within the hour.',
        'Hide the cash inside a magazine. The courier does not need to know.',
      ],
      rule:
        'Real police never ask you to withdraw money. Real banks never ask you to lie to their tellers. Real couriers never collect cash from your home.',
      ifAlreadyGone:
        'If a courier is on the way, do not open the door. Call 911. Then call your bank using the number on your card.',
    },
  },

  // Qatar face (Gulf English). Riyals in the banking mockup; each method
  // object is overridden whole because every one of them carries at least one
  // US-only line (gas station, title company, ACH / Zelle / FedNow, Bitcoin
  // ATMs, USPS, 911, FBI / FTC). Local rails: Fawran (QCB instant payments),
  // SWIFT for international, Qatar Post, MOI cybercrime unit, 999.
  // Crypto: Qatar does not license crypto payments and has no Bitcoin ATMs, so
  // the cash-at-an-ATM shape becomes exchange apps and peer-to-peer sellers.
  QA: {
    wireAmountPrefix: 'QAR',
    wireAmountWhole: '10,000',
    wireRemainingLimit: 'QAR 20,000.00',
    wireAccountName: 'Current Account (…1234)',
    wireAccountAvailable: 'QAR 185,000.00',

    giftCard: {
      label: 'Gift cards',
      title: 'Gift cards',
      intro:
        'Once you read out the code on the back, the money is gone in seconds. Cards are drained from anywhere in the world before you have left the shop.',
      whyScammers: [
        'The code on the back is the money. No bank account, no name, no trail.',
        'Available at every supermarket, petrol station and pharmacy.',
        'Drained instantly and converted to crypto on resale sites.',
        'No buyer protection. No refund. No chargeback.',
      ],
      inPractice: [
        'A caller stays on the line while you drive to the shop.',
        'They tell you exactly which brand to buy, which value, and how many cards.',
        'They ask you to scratch the back and read the 16-digit code aloud.',
        'Sometimes they ask for a photo of the back of the card.',
      ],
      phrases: [
        'Stay on the line. Do not tell the cashier why you are buying these.',
        'If anyone asks, say it is a present for your grandson.',
        'Just read me the numbers on the back. I will text you a receipt.',
      ],
      rule:
        'No real company, court, government agency or utility ever accepts payment in gift cards. Not once. Not ever.',
      ifAlreadyGone:
        'Call the gift card brand fraud line straight away. Apple, Amazon and Google can sometimes freeze the balance if you call within the first hour. Keep the card and the receipt as evidence.',
    },
    transfer: {
      label: 'Wire transfers',
      title: 'Wire transfers',
      intro:
        'Bank transfers look ordinary, which is exactly why scammers use them. Once the receiving bank releases the funds, recall is rare.',
      whyScammers: [
        'Looks like a normal payment, so banks may not flag it.',
        'Money can clear in minutes and cross borders within hours.',
        'Funds are split across mule accounts the moment they land.',
        'Pressure gets the transfer away before you double-check.',
      ],
      inPractice: [
        'Caller pretends to be your bank fraud team, asking you to "move money to a safe account".',
        'A real estate agent or property developer emails new bank details for a property payment.',
        'Romance partner needs emergency money for travel or a hospital bill.',
        'Investment "advisor" rushes a same-day transfer for a "limited window".',
      ],
      channels: {
        heading: 'Channels they ask you to use',
        items: [
          'Local bank transfer to a brand new payee',
          'International wire transfer (SWIFT) to an account abroad',
          'Western Union or MoneyGram (cash pickup, hardest to recover)',
          'Fawran instant transfers, which cannot be reversed once sent',
        ],
      },
      phrases: [
        'Do not tell your bank what it is for. They are part of the investigation.',
        'Move it now, you can move it back tomorrow.',
        'Use a new payee. The old account has been compromised.',
        'Mark the reason as "family" so it goes through faster.',
      ],
      rule:
        'Real banks never call to ask you to move money. Real estate agents never email new bank details out of the blue. Anyone asking for either is a scammer.',
      ifAlreadyGone:
        'Call your bank fraud line in the same hour. International wires can sometimes be recalled within 24 hours via SWIFT recall. Report the loss to the Ministry of Interior cybercrime unit (hotline 2347444 or the Metrash2 app), and contact Qatar Central Bank if your bank does not help.',
    },
    crypto: {
      label: 'Cryptocurrency',
      title: 'Cryptocurrency',
      intro:
        'Crypto payments are final. There is no fraud department to reverse them and no name attached to the receiving wallet.',
      whyScammers: [
        'No reversal. Once it leaves your wallet, it is gone.',
        'Wallets are anonymous strings of letters, not accounts in someone’s name.',
        'Peer-to-peer sellers convert a bank transfer to crypto in minutes.',
        'Funds split across hundreds of wallets in seconds.',
      ],
      inPractice: [
        'Caller directs you to buy crypto through an exchange app or a peer-to-peer seller.',
        'They send you a QR code. You scan it, send the payment, and the crypto goes straight to them.',
        '"Recovery agents" appear after a previous scam, asking for crypto to "unlock" your funds.',
        'Investment "advisor" on WhatsApp shows fake trading dashboards to keep you topping up.',
      ],
      channels: {
        heading: 'Where they send you',
        items: [
          'International exchange apps such as Binance, Coinbase or Kraken',
          'Peer-to-peer (P2P) sellers who take a bank transfer and send crypto',
          'Wallet apps like Trust Wallet or MetaMask asked to scan a QR code',
          'Bitcoin ATMs if you are travelling abroad',
        ],
      },
      phrases: [
        'Buy the coins, then send me a screenshot of the transfer.',
        'Your money will be safer in a government Bitcoin wallet.',
        'We can recover your lost crypto for a small gas fee.',
        'Do not tell your bank what the transfer is for.',
      ],
      rule:
        'No government, bank, utility or law enforcement agency accepts cryptocurrency. It is not a licensed payment method in Qatar, and no official body will ever ask you for it.',
      ifAlreadyGone:
        'Save the receiving wallet address and the transaction ID. Report to the Ministry of Interior cybercrime unit and to the exchange if you know it. Never pay a "recovery service". They are nearly always the same scammers under a new name.',
    },
    cashPost: {
      label: 'Cash by post or courier',
      title: 'Cash by post or courier',
      intro:
        'Cash leaves no paper trail and no banking protection at all. Once it is in a courier’s hands, it cannot be recalled.',
      whyScammers: [
        'No bank, no record, no fraud team to call.',
        'Bypasses the "are you sure?" warnings cashiers and apps now show.',
        'Couriers rarely check what is inside a parcel.',
        'Once collected, the scammer simply disappears.',
      ],
      inPractice: [
        'Caller pretends to be police and asks you to withdraw savings to "help catch a fraudster".',
        'A taxi or "plain-clothes officer" is sent to collect cash from your home.',
        'You are asked to post cash hidden inside a book, magazine or greetings card.',
        'Caller stays on the line during the bank visit and tells you what to say to staff.',
      ],
      channels: {
        heading: 'How they collect it',
        items: [
          'Couriers, often a local taxi or delivery driver, sent to your front door',
          'Qatar Post or DHL parcels with cash hidden inside other items',
          'Drop-off at a "safe" location like a mall car park',
          'Handover to a "plain-clothes officer" on your doorstep',
        ],
      },
      phrases: [
        'Tell the cashier the money is for home improvements.',
        'The notes will be marked, and returned to you within 48 hours.',
        'An officer named [first name] will arrive within the hour.',
        'Hide the cash inside a magazine. The courier does not need to know.',
      ],
      rule:
        'Real police never ask you to withdraw money. Real banks never ask you to lie to their tellers. Real couriers never collect cash from your home.',
      ifAlreadyGone:
        'If a courier is on the way, do not open the door. Call 999. Then call your bank using the number on your card.',
    },
  },

  ar: {
    heading: 'طرق دفع لا يمكن تتبّعها',
    intro:
      'يستخدم المحتالون طرق الدفع هذه لأن التراجع عنها شبه مستحيل. لن تطلب منك أي جهة تجارية أو حكومية موثوقة الدفع بهذه الطريقة أبداً.',
    learnMore: 'اعرف المزيد',
    panelEyebrow: 'تحذير بشأن الدفع',
    closeLabel: 'إغلاق اللوحة',
    whyHeading: 'لماذا يفضّلها المحتالون',
    whatHeading: 'كيف تبدو في الواقع',
    phrasesHeading: 'عبارات يجب الانتباه إليها',
    phraseQuoteOpen: '"',
    phraseQuoteClose: '"',
    ruleLabel: 'القاعدة',
    goneLabel: 'إذا كانت الأموال قد ذهبت بالفعل',

    giftCardVisualTitle: 'البطاقات التي يطلبها المحتالون',
    giftCardAriaLabel: 'بطاقة هدايا {brand}',
    giftCardVisualNote:
      'هذه هي العلامات التجارية الأكثر طلباً في بلاغات الاحتيال. يريدها المحتالون لأن أكوادها قابلة للاستخدام في أي مكان في العالم ويكاد يستحيل استردادها.',

    wireVisualTitle: 'كيف قد يبدو الأمر في تطبيق بنكك',
    wireAppTitle: 'تحويل الأموال',
    wireSendTo: 'إلى: بنك المستفيد',
    wireExternalAccount: 'حساب خارجي (…4873)',
    wireAmountPrefix: '',
    wireAmountWhole: '10,000',
    wireAmountCents: '.00',
    wireAmountSuffix: 'ر.ق',
    wireRemainingLabel: 'الحد المتبقي للتحويل: ',
    wireRemainingLimit: '20,000.00 ر.ق',
    wireFromLabel: 'من',
    wireAccountName: 'الحساب الجاري (…1234)',
    wireAvailableLabel: 'المتاح للتحويل: ',
    wireAccountAvailable: '185,000.00 ر.ق',
    wireDeliveryLabel: 'التسليم',
    wireDeliveryValue: 'فوري',
    wireNewPayee: 'مستفيد جديد. لم تحوّل إلى هذا الحساب من قبل.',
    wireContinue: 'متابعة ‹',
    wireFooter:
      'إذا كان شخص غريب يرشدك عبر هذه الشاشة خطوة بخطوة، فأغلق الخط واتصل ببنكك بنفسك.',

    giftCard: {
      label: 'بطاقات الهدايا',
      title: 'بطاقات الهدايا',
      intro:
        'بمجرد أن تقرأ الرمز الموجود خلف البطاقة، تختفي الأموال في ثوانٍ. تُستنزف البطاقات من أي مكان في العالم قبل أن تغادر المتجر.',
      whyScammers: [
        'الرمز خلف البطاقة هو المال نفسه. لا حساب مصرفي، ولا اسم، ولا أثر.',
        'متوفرة في كل سوبرماركت ومحطة وقود وصيدلية.',
        'تُستنزف فوراً وتُحوَّل إلى عملات رقمية عبر مواقع إعادة البيع.',
        'لا حماية للمشتري. لا استرداد. لا إلغاء للعملية.',
      ],
      inPractice: [
        'يبقى المتصل على الخط بينما تقود سيارتك إلى المتجر.',
        'يخبرك بالضبط أي علامة تجارية تشتري، وبأي قيمة، وكم بطاقة.',
        'يطلب منك كشط الجهة الخلفية وقراءة الرمز المكوّن من 16 رقماً بصوت عالٍ.',
        'وأحياناً يطلب صورة للجهة الخلفية من البطاقة.',
      ],
      phrases: [
        'ابقَ على الخط. لا تخبر أمين الصندوق لماذا تشتري هذه البطاقات.',
        'إذا سألك أحد، قل إنها هدية لحفيدك.',
        'فقط اقرأ لي الأرقام الموجودة في الخلف. سأرسل لك إيصالاً برسالة نصية.',
      ],
      rule:
        'لا توجد شركة حقيقية أو محكمة أو جهة حكومية أو شركة خدمات تقبل الدفع ببطاقات الهدايا. ولا مرة واحدة. أبداً.',
      ifAlreadyGone:
        'اتصل فوراً بخط مكافحة الاحتيال الخاص بالعلامة التجارية للبطاقة. يمكن لآبل وأمازون وجوجل أحياناً تجميد الرصيد إذا اتصلت خلال الساعة الأولى. واحتفظ بالبطاقة والإيصال كدليل.',
    },
    transfer: {
      label: 'الحوالات المصرفية',
      title: 'الحوالات المصرفية',
      intro:
        'تبدو التحويلات المصرفية أمراً عادياً، وهذا بالضبط سبب استخدام المحتالين لها. فبمجرد أن يفرج البنك المستلم عن الأموال، يصبح استردادها نادراً.',
      whyScammers: [
        'تبدو كعملية دفع عادية، فقد لا يرصدها البنك.',
        'قد تُنفَّذ الحوالة في دقائق وتعبر الحدود خلال ساعات.',
        'تُوزَّع الأموال على حسابات وسيطة بمجرد وصولها.',
        'يدفعك الضغط إلى إتمام التحويل قبل أن تتحقق مرة أخرى.',
      ],
      inPractice: [
        'يدّعي المتصل أنه من فريق مكافحة الاحتيال في بنكك، ويطلب منك "نقل أموالك إلى حساب آمن".',
        'يرسل وكيل عقاري أو مطوّر عقاري بريداً إلكترونياً ببيانات مصرفية جديدة لدفعة عقارية.',
        'شريك عاطفي عبر الإنترنت يحتاج إلى مال عاجل للسفر أو لفاتورة مستشفى.',
        '"مستشار" استثماري يستعجلك لإجراء تحويل في اليوم نفسه بسبب "فرصة محدودة".',
      ],
      channels: {
        heading: 'القنوات التي يطلبون منك استخدامها',
        items: [
          'تحويل محلي إلى مستفيد جديد تماماً',
          'حوالة دولية (سويفت) إلى حساب في الخارج',
          'ويسترن يونيون أو موني جرام (استلام نقدي، والأصعب في الاسترداد)',
          'تحويلات "فوران" الفورية، التي لا يمكن التراجع عنها بعد إرسالها',
        ],
      },
      phrases: [
        'لا تخبر بنكك بسبب التحويل. فهم جزء من التحقيق.',
        'حوّلها الآن، ويمكنك إعادتها غداً.',
        'استخدم مستفيداً جديداً. فالحساب القديم تم اختراقه.',
        'اكتب سبب التحويل "عائلة" حتى يمر بسرعة أكبر.',
      ],
      rule:
        'البنوك الحقيقية لا تتصل بك أبداً لتطلب منك نقل أموالك. والوكلاء العقاريون الحقيقيون لا يرسلون بيانات مصرفية جديدة فجأة عبر البريد الإلكتروني. وكل من يطلب أياً منهما محتال.',
      ifAlreadyGone:
        'اتصل بخط مكافحة الاحتيال في بنكك خلال الساعة نفسها. يمكن أحياناً استرداد الحوالات الدولية خلال 24 ساعة عبر طلب استرداد سويفت. أبلغ عن الخسارة إلى إدارة مكافحة الجرائم الإلكترونية في وزارة الداخلية (الخط الساخن 2347444 أو تطبيق مطراش2)، وتواصل مع مصرف قطر المركزي إذا لم يساعدك بنكك.',
    },
    crypto: {
      label: 'العملات الرقمية',
      title: 'العملات الرقمية',
      intro:
        'مدفوعات العملات الرقمية نهائية. لا توجد إدارة لمكافحة الاحتيال يمكنها التراجع عنها، ولا اسم مرتبط بالمحفظة المستلمة.',
      whyScammers: [
        'لا تراجع. بمجرد أن تغادر محفظتك، تختفي.',
        'المحافظ سلاسل مجهولة من الأحرف والأرقام، وليست حسابات باسم شخص.',
        'يحوّل البائعون بين الأفراد التحويل المصرفي إلى عملات رقمية في دقائق.',
        'تُوزَّع الأموال على مئات المحافظ في ثوانٍ.',
      ],
      inPractice: [
        'يوجّهك المتصل إلى شراء العملات الرقمية عبر تطبيق تداول أو بائع بين الأفراد.',
        'يرسل لك رمز QR. تمسحه، وترسل الدفعة، فتذهب العملات الرقمية إليه مباشرة.',
        '"وكلاء استرداد" يظهرون بعد عملية احتيال سابقة، ويطلبون عملات رقمية "للإفراج" عن أموالك.',
        '"مستشار" استثماري على واتساب يعرض لوحات تداول مزيّفة ليبقيك تضيف المزيد.',
      ],
      channels: {
        heading: 'إلى أين يرسلونك',
        items: [
          'تطبيقات تداول دولية مثل Binance أو Coinbase أو Kraken',
          'بائعون بين الأفراد (P2P) يستلمون تحويلاً مصرفياً ويرسلون عملات رقمية',
          'تطبيقات محافظ مثل Trust Wallet أو MetaMask مع طلب مسح رمز QR',
          'أجهزة صراف البيتكوين إذا كنت مسافراً في الخارج',
        ],
      },
      phrases: [
        'اشترِ العملات، ثم أرسل لي لقطة شاشة للتحويل.',
        'ستكون أموالك أكثر أماناً في محفظة بيتكوين حكومية.',
        'يمكننا استرداد عملاتك الرقمية المفقودة مقابل رسوم شبكة بسيطة.',
        'لا تخبر بنكك بسبب التحويل.',
      ],
      rule:
        'لا تقبل أي جهة حكومية أو بنك أو شركة خدمات أو جهة إنفاذ قانون العملات الرقمية. فهي ليست وسيلة دفع مرخّصة في قطر، ولن تطلبها منك أي جهة رسمية أبداً.',
      ifAlreadyGone:
        'احتفظ بعنوان المحفظة المستلمة ورقم المعاملة. أبلغ إدارة مكافحة الجرائم الإلكترونية في وزارة الداخلية، ومنصة التداول إذا كنت تعرفها. لا تدفع أبداً لأي "خدمة استرداد". فهم في الغالب المحتالون أنفسهم باسم جديد.',
    },
    cashPost: {
      label: 'النقد عبر البريد أو المندوب',
      title: 'النقد عبر البريد أو المندوب',
      intro:
        'النقد لا يترك أي أثر ورقي ولا يتمتع بأي حماية مصرفية على الإطلاق. وبمجرد أن يصل إلى يد المندوب، لا يمكن استرداده.',
      whyScammers: [
        'لا بنك، ولا سجل، ولا فريق مكافحة احتيال يمكنك الاتصال به.',
        'يتجاوز تحذيرات "هل أنت متأكد؟" التي يعرضها الآن الصرافون والتطبيقات.',
        'نادراً ما يتحقق المندوبون مما بداخل الطرد.',
        'وبمجرد الاستلام، يختفي المحتال ببساطة.',
      ],
      inPractice: [
        'يدّعي المتصل أنه من الشرطة ويطلب منك سحب مدخراتك "للمساعدة في القبض على محتال".',
        'يُرسَل سائق تاكسي أو "ضابط بملابس مدنية" لاستلام النقد من منزلك.',
        'يُطلب منك إرسال نقد بالبريد مخبأ داخل كتاب أو مجلة أو بطاقة تهنئة.',
        'يبقى المتصل على الخط أثناء زيارتك للبنك ويملي عليك ما تقوله للموظفين.',
      ],
      channels: {
        heading: 'كيف يستلمونه',
        items: [
          'مندوبون، غالباً سائق تاكسي أو سائق توصيل محلي، يُرسَلون إلى باب منزلك',
          'طرود عبر بريد قطر أو DHL مع نقد مخبأ داخل أغراض أخرى',
          'تسليم في مكان "آمن" مثل موقف سيارات أحد المجمعات التجارية',
          'تسليم إلى "ضابط بملابس مدنية" عند باب منزلك',
        ],
      },
      phrases: [
        'أخبر الصراف أن المال لتجديد المنزل.',
        'ستُوضع علامات على الأوراق النقدية، وستُعاد إليك خلال 48 ساعة.',
        'سيصل ضابط يُدعى [الاسم الأول] خلال ساعة.',
        'أخفِ النقد داخل مجلة. لا داعي لأن يعرف المندوب.',
      ],
      rule:
        'الشرطة الحقيقية لا تطلب منك أبداً سحب أموالك. والبنوك الحقيقية لا تطلب منك أبداً الكذب على موظفيها. ومندوبو التوصيل الحقيقيون لا يستلمون النقد من منزلك أبداً.',
      ifAlreadyGone:
        'إذا كان المندوب في طريقه إليك، فلا تفتح الباب. اتصل بالرقم 999. ثم اتصل ببنكك على الرقم الموجود على بطاقتك.',
    },
  },
}
